// The encrypted desk vault: named JSON values sealed with the device's
// non-extractable AES-GCM key (crypto.ts) and stored in IndexedDB (idb.ts).
//
// The vault is bound to one account: `meta.owner` records the user id the key
// was generated for. Opening it as anyone else, or from a build whose
// `DESK_STORE_VERSION` differs from the stored one, reports a `conflict`
// instead of silently reusing or discarding the data — the caller decides,
// behind a modal if unsynced events are in there (plan "Offline").

import { generateDeskKey, openJson, sealJson, type SealedValue } from './crypto';
import { DESK_DB_NAME, deleteDeskDb, idbDelete, idbGet, idbPut, openDeskDb } from './idb';

/** Bump when the shape of anything persisted in the vault changes. A
 * mismatch on boot is one of the wipeLocalState() triggers. */
export const DESK_STORE_VERSION = 2;

/** v1 records (Task 14 before review round 1) were sealed without AAD. They
 * are still READABLE (AES-GCM with no additional data = empty AAD) so a v1
 * vault with unsynced events raises the version-mismatch blocker listing
 * them — never a silent reset. Never written any more. */
const LEGACY_NO_AAD_VERSION = 1;

export type VaultName = 'query-client' | 'attention' | 'desk-log' | 'qr-seen';

export type VaultConflict = 'other_owner' | 'version_mismatch' | null;

export class DeskVault {
  private constructor(
    private readonly db: IDBDatabase,
    private readonly key: CryptoKey,
    readonly ownerId: string,
    readonly conflict: VaultConflict,
    /** The version the stored records were sealed under (= this build's
     * unless `conflict === 'version_mismatch'`). */
    private readonly storedVersion: number,
  ) {}

  /** Opens (creating on first use) the vault for `userId`. A fresh device
   * gets a newly generated key. */
  static async open(userId: string, currentVersion: number = DESK_STORE_VERSION): Promise<DeskVault> {
    const db = await openDeskDb();
    let key = await idbGet<CryptoKey>(db, 'meta', 'key');
    let owner = await idbGet<string>(db, 'meta', 'owner');
    const version = await idbGet<number>(db, 'meta', 'version');

    if (!key) {
      key = await generateDeskKey();
      owner = userId;
      await idbPut(db, 'meta', 'key', key);
      await idbPut(db, 'meta', 'owner', userId);
      await idbPut(db, 'meta', 'version', currentVersion);
      return new DeskVault(db, key, userId, null, currentVersion);
    }

    let conflict: VaultConflict = null;
    if (owner !== userId) conflict = 'other_owner';
    else if (version !== currentVersion) conflict = 'version_mismatch';
    return new DeskVault(db, key, owner ?? '', conflict, version ?? -1);
  }

  /** Read-only view of an existing vault for a tab that does not hold the
   * desk (a follower): never creates a key or writes anything. Null when
   * this browser has no desk database/key. */
  static async peek(): Promise<DeskVault | null> {
    // Don't create an empty database just by looking.
    if (typeof indexedDB.databases === 'function') {
      const existing = await indexedDB.databases();
      if (!existing.some((d) => d.name === DESK_DB_NAME)) return null;
    }
    const db = await openDeskDb();
    const key = await idbGet<CryptoKey>(db, 'meta', 'key');
    if (!key) {
      db.close();
      return null;
    }
    const owner = (await idbGet<string>(db, 'meta', 'owner')) ?? '';
    const version = (await idbGet<number>(db, 'meta', 'version')) ?? -1;
    return new DeskVault(db, key, owner, version === DESK_STORE_VERSION ? null : 'version_mismatch', version);
  }

  /** AAD for a record: binds it to its slot, its owner and the store
   * version, so meta.owner/meta.version are authenticated too. */
  private context(name: VaultName): string {
    if (this.storedVersion === LEGACY_NO_AAD_VERSION) return '';
    return `${name}|${this.ownerId}|${this.storedVersion}`;
  }

  async read<T>(name: VaultName): Promise<T | undefined> {
    const sealed = await idbGet<SealedValue>(this.db, 'vault', name);
    if (!sealed) return undefined;
    return openJson<T>(this.key, sealed, this.context(name));
  }

  async write(name: VaultName, value: unknown): Promise<void> {
    await idbPut(this.db, 'vault', name, await sealJson(this.key, value, this.context(name)));
  }

  async remove(name: VaultName): Promise<void> {
    await idbDelete(this.db, 'vault', name);
  }

  /** The raw sealed record — tests use it to prove nothing is stored in
   * clear. */
  rawRecord(name: VaultName): Promise<SealedValue | undefined> {
    return idbGet<SealedValue>(this.db, 'vault', name);
  }

  close(): void {
    this.db.close();
  }

  /** Closes this connection and deletes the whole desk database. */
  async destroy(): Promise<void> {
    this.db.close();
    await deleteDeskDb();
  }
}
